"use strict";
class FashionsBusiness extends HttpRequest {
  #businessPageContainer = document.querySelector(".business-fashions-page");
  #businessListContainer = this.#businessPageContainer.querySelector("#business-list-container");
  #businessAsideContainer = this.#businessPageContainer.querySelector(".aside-section");
  #businessType = this.#businessPageContainer.querySelector("#businessType") ? +this.#businessPageContainer.querySelector("#businessType")?.value : "";
  #fashionsSearchForm = this.#businessPageContainer.querySelector("#fashionsSearchForm");
  #loadingOverlay = this.#businessPageContainer.querySelector("#loading-scroll-overlay");
  #page = 0;
  #limit = 10;
  #observer = null;
  #hasMore = true;
  #isLoading = false;
  #totalLoadedData = 0;
  constructor() {
    super();
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  async #getBusinessListPagination(page = 0) {
    if (!this.#businessType) {
      return Promise.reject({
        status: "error",
        message: "אירעה שגיאה בטעינת רשימת העסקים",
      });
    }

    const formData = new FormData(this.#fashionsSearchForm);
    formData.append("module", "Business");
    formData.append("action", "GetWebsiteBusinessList");
    formData.append("business_type_id", this.#businessType);
    formData.append("page", page);
    formData.append("limit", this.#limit);

    const selectedTypes = Array.from(this.#businessAsideContainer.querySelectorAll('[name="priceRange"]:checked')).map((o) => o.value);
    if (selectedTypes.length) {
      formData.append("prices_range", selectedTypes.join(","));
    }

    const ratings = Array.from(this.#businessAsideContainer.querySelectorAll('input[name="ratingFilter"]:checked')).map((input) => input.value);
    if (ratings.length) {
      formData.append("ratings", ratings.join(","));
    }

    this.#loadingOverlay.classList.remove("hide");
    this.#loadingOverlay.classList.add("show");
    return this.FormRequest(formData, "fashionsSearchForm_");
  }

  #initInfiniteScroll() {
    const sentinel = this.#businessPageContainer.querySelector("#scroll-sentinel");
    if (!sentinel) {
      return;
    }

    const checkIfIsFirstLoading = async () => {
      const hasFirstLoading = this.#businessListContainer.querySelector(".skeleton-container");
      if (hasFirstLoading) {
        if (this.#hasMore && !this.#isLoading) {
          await this.#loadNextPage({ firstRender: true });
        }
      }
    };

    this.#observer = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && this.#hasMore && !this.#isLoading) {
          await this.#loadNextPage();
        } else {
          checkIfIsFirstLoading();
        }
      },
      {
        rootMargin: "0px 0px 300px 0px",
        threshold: 0,
      }
    );

    this.#observer.observe(sentinel);
  }

  async #loadNextPage({ firstRender = false } = {}) {
    this.#isLoading = true;
    this.#page++;
    if (firstRender) {
      this.#page = 0;
    }
    try {
      const { data, hasMore } = await this.#getBusinessListPagination(this.#page);
      const list = Array.isArray(data) ? data : [];
      this.#totalLoadedData += list.length;
      if (!list?.length) {
        this.#hasMore = false;
        return;
      }
      for (const business of list) {
        this.#removeSkeletonCards();
        const card = this.#createBusinessCard(business);
        this.#businessListContainer.appendChild(card);
      }

      this.#hasMore = hasMore;
    } catch (err) {
      this.#removeSkeletonCards();
      this.#hasMore = false;
    } finally {
      this.#isLoading = false;
      this.#loadingOverlay.classList.remove("show");
      this.#loadingOverlay.style.height = "0";
      if (this.#totalLoadedData === 0) {
        this.#createEmptyCard();
      }

      !this.#hasMore ? this.#loadingOverlay.classList.add("d-none") : this.#loadingOverlay.classList.remove("d-none");
    }
  }

  #createBusinessCard(business) {
    const col = document.createElement("div");
    col.className = "col-12 mb-3";

    const card = document.createElement("div");
    card.className = "card business-card mb-3 overflow-hidden";

    const row = document.createElement("div");
    row.className = "row g-0";

    const imgCol = document.createElement("div");
    imgCol.className = "col-md-4 overflow-hidden";
    imgCol.style.maxHeight = "250px";
    const link = document.createElement("a");
    link.href = `${SITE_URL}${business.strid}/`;

    const coverWrapper = document.createElement("div");
    coverWrapper.className = "photo position-relative image-wrapper h-100";
    coverWrapper.style.backgroundColor = "transparent";

    const spinnerWrapper = document.createElement("div");
    spinnerWrapper.className = "spinner-wrapper loading-spinner";
    spinnerWrapper.style.cssText = "position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 10;";
    const spinner = document.createElement("div");
    spinner.className = "spinner-border text-danger";
    spinner.role = "status";
    const spinnerText = document.createElement("span");
    spinnerText.className = "visually-hidden";
    spinnerText.textContent = "Loading...";
    spinner.appendChild(spinnerText);
    spinnerWrapper.appendChild(spinner);
    coverWrapper.appendChild(spinnerWrapper);

    const skeleton = document.createElement("div");
    skeleton.className = "skeleton-item rounded-0 w-100 h-100 top-50 start-50 translate-middle";
    coverWrapper.appendChild(skeleton);
    const preview = BuildImageElement({
      id: "preview-image",
      src: `${business?.logo_file}100x100`,
      srcset: {
        100: `${business?.logo_file}100x100`,
        400: `${business?.logo_file}AiThumbCover`,
        1366: `${business?.logo_file}AiPublicScaleD`,
      },
      alt: business.title?.substring(0, 25) || "",
      fallback: `${CLOUD_PHOTO_URL}aip_broken-image-light/public`,
      className: "preview-image position-absolute top-0 start-0 logo-image shadow-5 m-1",
      style:["z-index:3", "width:80px", "height:80px", "clip-path:circle()", "box-shadow:0 10px 25px rgba(0,0,0,.15)", "background:#fff"].join(";"),
      loading: "lazy",
    });


    const fullImageWrapper = document.createElement("div");
    fullImageWrapper.className = "position-relative w-100 h-100";

    const fullImage = BuildImageElement({
      id: "full-image",
      src: `${business?.cover_file}AiSmallScaleD`,
      srcset: {
        100: `${business?.cover_file}AiSmallScaleD`,
        400: `${business?.cover_file}AiThumbCover`,
        1366: `${business?.cover_file}AiPublicScaleD`,
      },
      alt: business.title?.substring(0, 25) || "",
      fallback: `${CLOUD_PHOTO_URL}aip_broken-image-light/public`,
      className: "full-image w-100 h-100 position-relative hover-zoom",
      parentElement: coverWrapper,
      fallbackStyle: "object-fit: contain; padding: 20px;",
      loading: "lazy",
    });

    fullImage.style.objectFit = "cover";

    const mask = document.createElement("div");
    mask.className = "mask position-absolute top-0 start-0 w-100 h-100";
    mask.style.zIndex = "2";

    mask.style.background = "linear-gradient(180deg, rgba(0,0,0,.15) 0%, rgba(0,0,0,.25) 100%)";

    // append image then mask into wrapper
    fullImageWrapper.appendChild(fullImage);
    fullImageWrapper.appendChild(mask);

    // mount into cover area
    coverWrapper.append(preview, fullImageWrapper);
    link.append(coverWrapper);
    imgCol.append(link);

    const bodyCol = document.createElement("div");
    bodyCol.className = "col-md-8";
    const cardBody = document.createElement("div");
    cardBody.className = "card-body";

    const titleEl = document.createElement("h2");
    titleEl.className = "card-title";
    const titleLink = document.createElement("a");
    titleLink.href = `${SITE_URL}${business.strid}/`;
    titleLink.textContent = business.title;
    titleEl.appendChild(titleLink);
    cardBody.appendChild(titleEl);

    const about = document.createElement("p");
    about.className = "card-text";
    about.textContent = (business.about || "").substring(0, 150) + ((business.about || "").length > 150 ? "..." : "");
    cardBody.appendChild(about);

    const infoDiv = document.createElement("div");
    infoDiv.className = "card-text";

    const ratingSection = document.createElement("div");
    ratingSection.className = "rating-section d-flex align-items-center mb-2";
    const ratingList = document.createElement("ul");
    ratingList.className = "rating me-2";
    ratingList.setAttribute("data-mdb-rating-init", "");
    ratingList.setAttribute("data-mdb-readonly", "true");
    ratingList.setAttribute("data-mdb-value", business.rating || "0");
    const ratingValue = parseFloat(business.rating || 0);
    for (let i = 0; i < 5; i++) {
      const li = document.createElement("li");
      li.innerHTML = i < ratingValue ? '<i class="fa-solid fa-star fa-sm text-primary"></i>' : '<i class="far fa-star fa-sm text-primary"></i>';
      ratingList.appendChild(li);
    }
    const reviews = document.createElement("small");
    reviews.className = "text-muted";
    reviews.textContent = `${business.reviews || 0} חוות דעת`;
    ratingSection.append(ratingList, reviews);
    // infoDiv.appendChild(ratingSection);

    if (Array.isArray(business.services) && business.services.length) {
      const container = document.createElement("div");
      container.className = "d-flex align-items-center flex-nowrap overflow-x-auto";
      const limit = 10;
      business.services.slice(0, limit).forEach((service) => {
        const chip = document.createElement("span");
        chip.className = "chip chip-outline btn-outline-secondary pe-none me-2 mb-1";
        chip.style.whiteSpace = "nowrap";
        chip.setAttribute("data-mdb-chip-init", "");
        chip.setAttribute("data-mdb-ripple-color", "dark");
        chip.innerHTML = `</i>${service.trim()}`;
        container.appendChild(chip);
      });
      infoDiv.appendChild(container);
    }

    if (business.address) {
      const cardFooter = document.createElement("div");
      cardFooter.className = "card-footer";
      const addressSection = document.createElement("div");
      addressSection.className = "address-section w-100";
      const addrP = document.createElement("p");
      addrP.textContent = business.address;
      const hr = document.createElement("hr");
      hr.className = "w-100 mt-2";
      addressSection.append(hr, addrP);
      if (business.lat && business.lng) {
        const navLink = document.createElement("a");
        navLink.href = `https://www.google.com/maps/search/?api=1&query=${business.lat},${business.lng}`;
        navLink.target = "_blank";
        navLink.textContent = "ניווט לעסק";
        addressSection.appendChild(navLink);
      }
      infoDiv.appendChild(addressSection);
    }

    cardBody.appendChild(infoDiv);
    bodyCol.appendChild(cardBody);

    row.append(imgCol, bodyCol);

    card.appendChild(row);
    col.appendChild(card);

    return col;
  }

  #createSkeletonCard(count = 3) {
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < count; i++) {
      // Outer column
      const col = document.createElement("div");
      col.className = "col-12 mb-3 skeleton-container";

      // Card container
      const card = document.createElement("div");
      card.className = "card business-card mb-3 skeleton-loader";

      // Row wrapper
      const row = document.createElement("div");
      row.className = "row g-0 overflow-hidden";

      // Image column (left)
      const imgCol = document.createElement("div");
      imgCol.className = "col-md-4 overflow-hidden";

      const imgSkeleton = document.createElement("div");
      imgSkeleton.className = "w-100 h-100 skeleton-box";
      imgSkeleton.style.height = "200px";
      imgCol.appendChild(imgSkeleton);

      // Body column (right)
      const bodyCol = document.createElement("div");
      bodyCol.className = "col-md-8";

      const cardBody = document.createElement("div");
      cardBody.className = "card-body";

      const titleSkeleton = document.createElement("div");
      titleSkeleton.className = "skeleton-box mb-2";
      titleSkeleton.style.width = "60%";
      titleSkeleton.style.height = "24px";

      const textSkeleton1 = document.createElement("div");
      textSkeleton1.className = "skeleton-box mb-2";
      textSkeleton1.style.width = "90%";
      textSkeleton1.style.height = "16px";

      const textSkeleton2 = document.createElement("div");
      textSkeleton2.className = "skeleton-box mb-2";
      textSkeleton2.style.width = "75%";
      textSkeleton2.style.height = "16px";

      const tagSkeletonContainer = document.createElement("div");
      tagSkeletonContainer.className = "d-flex flex-wrap gap-2 mb-2";
      for (let j = 0; j < 3; j++) {
        const tag = document.createElement("div");
        tag.className = "skeleton-box";
        tag.style.width = "80px";
        tag.style.height = "24px";
        tagSkeletonContainer.appendChild(tag);
      }

      cardBody.append(titleSkeleton, textSkeleton1, textSkeleton2, tagSkeletonContainer);
      bodyCol.appendChild(cardBody);

      row.append(imgCol, bodyCol);
      card.appendChild(row);
      col.appendChild(card);

      fragment.appendChild(col);
    }
    this.#businessListContainer.innerHTML = "";
    this.#businessListContainer.appendChild(fragment);
  }

  #createEmptyCard(error = {}) {
    const card = document.createElement("div");
    card.className = "card business-card mb-3 empty-card";

    const cardBody = document.createElement("div");
    cardBody.className = "card-body p-4 d-flex flex-column align-items-center justify-content-center";

    const title = document.createElement("h5");
    title.className = "card-title";
    title.textContent = "אין תוצאות";

    const text = document.createElement("p");
    text.className = "card-text";
    text.textContent = "לא נמצאו עסקים מתאימים.";

    // כפתור רענון
    const refreshBtn = document.createElement("button");
    refreshBtn.className = "btn btn-outline-primary mt-2";
    refreshBtn.textContent = "רענן חיפוש";
    refreshBtn.onclick = async () => {
      this.#removeEmptyCard();
      this.#createSkeletonCard(4);
      await new Promise((resolve) => setTimeout(resolve, 500));
      this.#page = 0;
      this.#hasMore = true;
      this.#isLoading = false;
      this.#totalLoadedData = 0;
      this.#businessListContainer.innerHTML = "";
      this.#loadNextPage({ firstRender: true });
    };

    cardBody.appendChild(title);
    cardBody.appendChild(text);
    cardBody.appendChild(refreshBtn);
    card.appendChild(cardBody);

    this.#removeSkeletonCards();
    this.#businessListContainer.innerHTML = "";
    this.#businessListContainer.appendChild(card);
    this.#loadingOverlay.classList.add("d-none");
  }

  #removeSkeletonCards() {
    const skeletonContainer = this.#businessListContainer.querySelectorAll(".skeleton-container");
    if (!skeletonContainer) return;
    skeletonContainer.forEach((o) => o.remove());
  }

  #removeEmptyCard() {
    const emptyCard = this.#businessListContainer.querySelector(".card.business-card.empty-card");
    if (emptyCard) {
      emptyCard.remove();
    }
  }

  #submitFilterBusiness() {
    if (!this.#fashionsSearchForm) {
      return Toast({
        message: "התרחשה שגיאה בעת טעינת הדף",
        status: "error",
      });
    }
    const form = this.#fashionsSearchForm;
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!form.checkValidity()) {
        return form.classList.add("was-validated");
      }

      form.classList.remove("was-validated");
      ShowLoading(form.querySelector('button[type="submit"]'));
      try {
        this.#removeEmptyCard();
        this.#createSkeletonCard(4);
        await new Promise((resolve) => setTimeout(resolve, 500));
        this.#page = 0;
        this.#hasMore = true;
        this.#isLoading = false;
        this.#totalLoadedData = 0;
        await this.#loadNextPage({ firstRender: false });
        Toast({
          status: "success",
          color: "light",
          message: !this.#totalLoadedData ? "לא נמצאו תוצאות חיפוש" : `נמצאו ${this.#totalLoadedData} תוצאות חיפוש`,
          delay: 4000,
        });
      } catch (error) {
      } finally {
        HideLoading(form.querySelector('button[type="submit"]'));
      }
    });
  }

  async #initAsideMap() {
    /* init map */
    const mapElement = document.querySelector("#fashions-aside-map");
    $MapClass.setLatLng(DEFUALT_LATITUDE, DEFUALT_LONGITUDE);
    await $MapClass.initMap(mapElement, { zoom: 12 });

    /* full screen map */
    const mapElementFullScreen = document.querySelector(".full-screen-map .modal-body");
    await $MapClass.initMap(mapElementFullScreen, { zoom: 10 });
  }

  #initBusinessAsideFilterHandlers() {
    const container = this.#businessAsideContainer;
    if (!container) return;

    container.addEventListener("change", async (e) => {
      if (e.target.type !== "checkbox" && e.target.type !== "radio") return;

      ShowLoading(e.target);
      try {
        this.#removeEmptyCard();
        this.#createSkeletonCard(4);
        await new Promise((resolve) => setTimeout(resolve, 500));
        this.#page = 0;
        this.#hasMore = false;
        this.#isLoading = false;
        this.#totalLoadedData = 0;
        const result = await this.#loadNextPage({ firstRender: true });
      } catch (error) {
      } finally {
        HideLoading(e.target);
      }
    });
  }

  init() {
    this.#initAsideMap();
    this.#initInfiniteScroll();
    this.#initBusinessAsideFilterHandlers();
    this.#submitFilterBusiness();
  }
}

const $businessRomms = new FashionsBusiness();
$businessRomms.init();
